import { create } from "zustand";

export interface Toast {
  id: string;
  message: string;
  type: "success" | "error" | "info" | "warning";
  duration?: number;
}

const DEFAULT_DURATION = 3000;
let toastId = 0;

interface ToastState {
  toasts: Toast[];
  addToast: (
    message: string,
    type?: Toast["type"],
    duration?: number,
  ) => string;
  removeToast: (id: string) => void;
  success: (message: string, duration?: number) => string;
  error: (message: string, duration?: number) => string;
  info: (message: string, duration?: number) => string;
  warning: (message: string, duration?: number) => string;
  clear: () => void;
  hasActiveErrorToast: () => boolean;
}

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],

  hasActiveErrorToast: () => get().toasts.some((t) => t.type === "error"),

  addToast: (message, type = "info", duration = DEFAULT_DURATION) => {
    const id = `toast-${++toastId}`;
    const toast: Toast = { id, message, type, duration };
    set((s) => ({ toasts: [...s.toasts, toast] }));

    if (duration > 0) {
      setTimeout(() => get().removeToast(id), duration);
    }
    return id;
  },

  removeToast: (id) => {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },

  success: (message, duration) => get().addToast(message, "success", duration),

  error: (message, duration) => {
    // Avoid stacking multiple error toasts at the same time.
    if (get().hasActiveErrorToast()) {
      console.warn(
        "[useToast] Erro já em exibição, ignorando novo erro:",
        message,
      );
      return "";
    }
    return get().addToast(message, "error", duration);
  },

  info: (message, duration) => get().addToast(message, "info", duration),

  warning: (message, duration) => get().addToast(message, "warning", duration),

  clear: () => set({ toasts: [] }),
}));

/**
 * Hook for components. Returns the same API shape as the Vue composable
 * so consumer code can be ported without API changes.
 */
export function useToast() {
  const toasts = useToastStore((s) => s.toasts);
  const { addToast, removeToast, success, error, info, warning, clear, hasActiveErrorToast } =
    useToastStore.getState();
  return {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    info,
    warning,
    clear,
    hasActiveErrorToast,
  };
}
